import React, { useState } from 'react';
import { Dialog, DialogContent, DialogActions, Button, Typography, Box, IconButton, Tooltip, Slide, Avatar, TextField, FormControl, InputLabel, Select, MenuItem, Switch, FormControlLabel, Paper, Chip, Stack, Popover, InputAdornment } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import LibraryAddCheckIcon from '@mui/icons-material/LibraryAddCheck';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline';
import { useModalPagoMasivoController } from './hooks/useModalPagoMasivoController';
import SimpleSuccessSpinner from '../LoadingSpinner';
import { SectionCard } from '../ModalSolicitudes/components/SectionCard';

const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

const elegantInputSx = {
  '& .MuiOutlinedInput-root': {
    borderRadius: 1, backgroundColor: '#fff',
    '& fieldset': { borderColor: 'rgba(0,0,0,0.1)' },
    '&:hover fieldset': { borderColor: 'rgba(0,0,0,0.2)' },
    '&.Mui-focused fieldset': { borderColor: '#0655A5' },
  },
  '& .MuiInputBase-input': {
      fontSize: '0.85rem',
      padding: '8px 12px',
  },
  '& .MuiInputLabel-root': { fontWeight: 600, fontSize: '0.85rem', top: '-2px' },
};

const formatCurrency = (val) => new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN' }).format(val);

const safeParseMonto = (raw) => {
    const num = typeof raw === 'string' ? Number(raw.replace(/[^0-9.-]+/g,"")) : Number(raw);
    return Number.isFinite(num) ? formatCurrency(num) : (raw || '$0.00');
};

export default function ModalPagoMasivo({ open, handleCloseModal, selectedRows = [], onSaved }) {
    const [dragRowId, setDragRowId] = React.useState(null);

  const {
    rowsToProcess, pagosList, handleRowChange, handleRowFiles, removeRowFile, metodosPago, enviarCorreos, setEnviarCorreos,
    combinedErrors, loading, progress, autorizarPagosMasivos,
    handleRowReferenciaArrayChange, addRowReferenciaField, removeRowReferenciaField
  } = useModalPagoMasivoController(open, handleCloseModal, selectedRows, onSaved);

  const [refPopover, setRefPopover] = useState({ anchorEl: null, rowId: null });
  const openPop = Boolean(refPopover.anchorEl);

  const handleOpenRefPop = (event, id) => {
    setRefPopover({ anchorEl: event.currentTarget, rowId: id });
  };
  const handleCloseRefPop = () => {
    setRefPopover({ anchorEl: null, rowId: null });
  };

  return (
    <>
      <Dialog open={open} disableRestoreFocus onClose={!loading ? handleCloseModal : undefined} maxWidth="lg" fullWidth TransitionComponent={Transition} PaperProps={{ sx: { borderRadius: 3, boxShadow: '0 24px 64px rgba(0,0,0,.2)', bgcolor: '#f4f6f8' } }}>
        <Box sx={{ p: 2, background: 'linear-gradient(135deg, #09814A 0%, #055C33 100%)', color: 'white', display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Avatar sx={{ bgcolor: 'rgba(255,255,255,0.15)', color: 'white', width: 40, height: 40 }}>
            <LibraryAddCheckIcon />
          </Avatar>
          <Box sx={{ flex: 1 }}>
            <Typography variant="h6" sx={{ fontWeight: 800, lineHeight: 1.1 }}>Comprobantes masivos</Typography>
            <Typography variant="caption" sx={{ opacity: 0.9 }}>
              Completar la información para poner al corriente las {rowsToProcess.length} OC(s) seleccionadas.
            </Typography>
          </Box>
          <Tooltip title="Cerrar">
             <span>
                <IconButton onClick={handleCloseModal} disabled={loading} sx={{ color: 'white' }}><CloseIcon /></IconButton>
             </span>
          </Tooltip>
        </Box>

        <DialogContent sx={{ p: 3 }}>
          <SectionCard title="Notificaciones Generales" subtitle="Aplicable a todas las filas de abajo" sx={{ mb: 3, bgcolor: '#fffbed', borderColor: '#ffe58f', p: 1.5 }}>
             <FormControlLabel 
                control={
                    <Switch 
                        checked={enviarCorreos} 
                        onChange={(e) => setEnviarCorreos(e.target.checked)} 
                        color="success"
                    />
                } 
                label={(
                    <Box>
                        <Typography sx={{ fontWeight: 600, fontSize: '0.9rem' }}>Enviar correos de confirmación</Typography>
                        <Typography variant="caption" sx={{ color: 'text.secondary' }}>Si lo activas, el sistema avisará por correo a los compradores que su OC ya fue pagada con su respectivo comprobante.</Typography>
                    </Box>
                )} 
             />
          </SectionCard>

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {rowsToProcess.map((row) => {
                  const data = pagosList[row.ID] || {};
                  const errs = combinedErrors[row.ID] || {};
                  const met = metodosPago.find(m => String(m.METODOPAGOID) === String(data.metodoPago));
                  const label = (met?.METODOPAGO || '').toUpperCase();
                  
                   const isTransfer = label.includes('TRANSFERENCIA');
                   const isOptional = label.includes('EFECTIVO') || label.includes('REFERENCIA') || label.includes('TARJETA');
                   
                   let reqRef = Number(met?.REQUIERE_REFERENCIA) === 1;
                   let reqComp = Number(met?.REQUIERE_COMPROBANTE) === 1 || isTransfer || isOptional;
                   const compMandatory = isTransfer;

                  return (
                      <Paper key={row.ID} elevation={0} sx={{ p: 2, borderRadius: 2, border: '1px solid', borderColor: Object.keys(errs).length ? '#ef5350' : '#e0e0e0', position: 'relative' }}>
                           <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1, alignItems: 'center' }}>
                               <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                  <Typography sx={{ fontWeight: 800, color: '#09814A', fontSize: '0.95rem' }}>OC: {row.FOLIO}</Typography>
                                  {compMandatory && !data.files?.length && (
                                      <Chip label="REQUERIDO" size="small" color="error" sx={{ height: 20, fontSize: '0.6rem', fontWeight: 900 }} />
                                  )}
                               </Box>
                               <Typography sx={{ fontWeight: 700, color: '#333', fontSize: '0.95rem' }}>{safeParseMonto(row.MONTOSOLICITADO)}</Typography>
                           </Box>
                          
                          <Typography variant="caption" sx={{ display: 'block', mb: 2, color: '#666' }}>
                              Solicitante: {row.SOLICITANTE} | CC: {row.CENTROCOSTO} ({row.DIVISA}) | Proveedor: {row.PROVEEDOR_NOMBRE || row.EMPRESA}
                          </Typography>

                          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center' }}>
                              <TextField 
                                  size="small" type="date" label="Fecha de Pago" 
                                  value={data.fechaPago || ''} 
                                  onChange={(e) => handleRowChange(row.ID, 'fechaPago', e.target.value)} 
                                  InputLabelProps={{ shrink: true }} sx={{ ...elegantInputSx, flex: '1 1 120px' }} 
                                  error={Boolean(errs.fechaPago)} 
                              />
                              
                              <FormControl size="small" sx={{ ...elegantInputSx, flex: '1 1 200px' }} error={Boolean(errs.metodoPago)}>
                                <InputLabel>Método</InputLabel>
                                 <Select value={data.metodoPago || ''} onChange={(e) => handleRowChange(row.ID, 'metodoPago', e.target.value)} label="Método" MenuProps={{ disableRestoreFocus: true }}>
                                  {metodosPago.map(m => <MenuItem key={m.METODOPAGOID} value={m.METODOPAGOID}>{m.METODOPAGO}</MenuItem>)}
                                </Select>
                              </FormControl>

                              <TextField 
                                  size="small" label="Referencia" 
                                  value={data.referencia || ''} 
                                  onChange={(e) => handleRowChange(row.ID, 'referencia', e.target.value)} 
                                  sx={{ ...elegantInputSx, flex: '1 1 180px' }} 
                                  disabled={!reqRef}
                                  error={Boolean(errs.referencia) || (data.referencia?.length > 255)} 
                                  placeholder={!reqRef ? "N/A" : ""}
                                  InputProps={{
                                    endAdornment: reqRef && (
                                      <InputAdornment position="end">
                                        <Tooltip title="Múltiples referencias">
                                          <IconButton size="small" onClick={(e) => handleOpenRefPop(e, row.ID)} edge="end">
                                            <AddCircleOutlineIcon fontSize="small" color="primary" />
                                          </IconButton>
                                        </Tooltip>
                                      </InputAdornment>
                                    )
                                  }}
                                  helperText={(data.referencia?.length > 255) ? "Máx 255" : ""}
                              />

                              <TextField 
                                  size="small" label="Observaciones" 
                                  value={data.observaciones || ''} 
                                  onChange={(e) => handleRowChange(row.ID, 'observaciones', e.target.value)} 
                                  sx={{ ...elegantInputSx, flex: '1 1 250px' }} 
                              />

                               <Box 
                                  sx={{ 
                                      flex: '1 1 200px', 
                                      display: 'flex', 
                                      flexDirection: 'column', 
                                      pt: 0.5,
                                      border: dragRowId === row.ID ? '2px dashed #09814A' : '2px dashed transparent',
                                      borderRadius: 2,
                                      bgcolor: dragRowId === row.ID ? 'rgba(9, 129, 74, 0.05)' : 'transparent',
                                      transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                                      minHeight: (data.files && data.files.length > 0) ? 'auto' : 60,
                                      justifyContent: (data.files && data.files.length > 0) ? 'flex-start' : 'center',
                                      px: 1,
                                      cursor: reqComp ? 'pointer' : 'default'
                                  }}
                                  onDragOver={(e) => {
                                      e.preventDefault();
                                      if (reqComp) setDragRowId(row.ID);
                                  }}
                                  onDragLeave={() => setDragRowId(null)}
                                  onDrop={(e) => {
                                      e.preventDefault();
                                      setDragRowId(null);
                                      if (reqComp && e.dataTransfer.files?.length) {
                                          handleRowFiles(row.ID, e.dataTransfer.files);
                                      }
                                  }}
                              >
                                  <input 
                                     type="file" 
                                     multiple
                                     id={`file-${row.ID}`} 
                                     style={{ display: 'none' }} 
                                     onChange={(e) => {
                                         if (e.target.files?.length) {
                                            handleRowFiles(row.ID, e.target.files);
                                         }
                                         e.target.value = '';
                                     }}
                                  />
                                  <Button 
                                      size="small" 
                                      component="span" 
                                      variant={data.files?.length ? "contained" : "outlined"} 
                                      color={errs.files ? "error" : (compMandatory ? "error" : "info")}
                                      startIcon={<AttachFileIcon />}
                                      onClick={() => document.getElementById(`file-${row.ID}`)?.click()}
                                      sx={{ 
                                          textTransform: 'none', 
                                          fontSize: '0.7rem',
                                          py: 0.5,
                                          fontWeight: 800,
                                          borderRadius: 2,
                                          borderWidth: 2,
                                          '&:hover': { borderWidth: 2 }
                                      }}
                                  >
                                      {data.files?.length ? `${data.files.length} Archivo(s)` : (compMandatory ? "SUBIR OBLIGATORIO" : "SUBIR OPCIONAL")}
                                  </Button>
                                  {errs.files && <Typography variant="caption" color="error" sx={{ mt: 0.5, lineHeight: 1 }}>{errs.files}</Typography>}
                                  
                                  {(data.files && data.files.length > 0) && (
                                     <Stack direction="row" spacing={0.5} sx={{ flexWrap: 'wrap', mt: 1 }}>
                                        {data.files.map((f, fIdx) => (
                                           <Chip 
                                               key={fIdx} 
                                               size="small"
                                               label={f.name.length > 12 ? f.name.substring(0, 10)+'...' : f.name} 
                                               onDelete={() => removeRowFile(row.ID, fIdx)} 
                                               sx={{ height: 20, fontSize: '0.65rem' }} 
                                           />
                                        ))}
                                     </Stack>
                                  )}
                              </Box>
                          </Box>
                      </Paper>
                  )
              })}
          </Box>
        </DialogContent>

        <DialogActions sx={{ p: 2, borderTop: '1px solid #ddd', bgcolor: '#fff' }}>
          <Button onClick={handleCloseModal} disabled={loading} sx={{ color: 'text.secondary', fontWeight: 600 }}>Cancelar</Button>
          <Button variant="contained" color="success" onClick={autorizarPagosMasivos} disabled={loading} sx={{ px: 4, borderRadius: 8 }}>
            Procesar Modalidad Fila x Fila ({rowsToProcess.length})
          </Button>
        </DialogActions>
      </Dialog>
      <SimpleSuccessSpinner open={loading} handleClose={() => null} message={`Procesando pagos (${progress.current}/${progress.total})...`} />
        
        {/* Popover para Referencias Múltiples */}
        <Popover
          open={openPop}
          anchorEl={refPopover.anchorEl}
          onClose={handleCloseRefPop}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          transformOrigin={{ vertical: 'top', horizontal: 'right' }}
          PaperProps={{ sx: { p: 2, width: 320, borderRadius: 2, boxShadow: '0 8px 32px rgba(0,0,0,0.15)' } }}
        >
          <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 800, color: '#0655A5', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            Referencias Múltiples
            <IconButton size="small" onClick={() => addRowReferenciaField(refPopover.rowId)} color="primary">
              <AddCircleOutlineIcon fontSize="small" />
            </IconButton>
          </Typography>
          
          <Stack spacing={1.5}>
            {(pagosList[refPopover.rowId]?.referenciasArray || ['']).map((refVal, idx) => (
              <Box key={idx} sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                <TextField 
                  size="small" 
                  fullWidth 
                  value={refVal} 
                  autoFocus={idx > 0}
                  onChange={(e) => handleRowReferenciaArrayChange(refPopover.rowId, idx, e.target.value)}
                  sx={elegantInputSx}
                  placeholder={`Ref ${idx + 1}`}
                />
                {(pagosList[refPopover.rowId]?.referenciasArray || []).length > 1 && (
                  <IconButton size="small" color="error" onClick={() => removeRowReferenciaField(refPopover.rowId, idx)}>
                    <RemoveCircleOutlineIcon fontSize="small" />
                  </IconButton>
                )}
              </Box>
            ))}
          </Stack>

          <Box sx={{ mt: 2, pt: 1, borderTop: '1px dashed #eee', display: 'flex', justifyContent: 'space-between' }}>
             <Typography variant="caption" sx={{ fontWeight: 700, color: (pagosList[refPopover.rowId]?.referencia?.length > 255) ? '#d32f2f' : '#666' }}>
               Total: {pagosList[refPopover.rowId]?.referencia?.length || 0} / 255
             </Typography>
             {(pagosList[refPopover.rowId]?.referencia?.length > 255) && (
               <Typography variant="caption" sx={{ color: '#d32f2f', fontWeight: 800 }}>
                 ⚠️ Excedido
               </Typography>
             )}
          </Box>
        </Popover>
    </>
  );
}
